import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { CertifiedTrainerApplication } from '../../types';
import ConfirmModal from '../../components/common/ConfirmModal';
import { useToast } from '../../hooks/useToast';

export default function CertifiedTrainerApplications() {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const [applications, setApplications] = useState<CertifiedTrainerApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [selectedApp, setSelectedApp] = useState<CertifiedTrainerApplication | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant?: 'danger' | 'warning' | 'info';
  } | null>(null);

  useEffect(() => {
    loadApplications();
  }, []);

  async function loadApplications() {
    try {
      setLoading(true);
      const response = await api.get('/admin/trainer-applications');
      setApplications(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || t('trainerApplications.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }

  function handleApprove(applicationId: string) {
    setConfirmModal({
      isOpen: true,
      title: t('trainerApplications.approveTitle'),
      message: t('trainerApplications.approveMessage'),
      variant: 'info',
      onConfirm: async () => {
        setProcessing(true);
        try {
          await api.post(`/admin/trainer-applications/${applicationId}/approve`);
          loadApplications();
          setSelectedApp(null);
        } catch (err: any) {
          toast.error(err.response?.data?.error || t('trainerApplications.failedToApprove'));
        } finally {
          setProcessing(false);
          setConfirmModal(null);
        }
      },
    });
  }

  async function handleReject(applicationId: string) {
    if (!rejectionReason.trim()) {
      toast.warning(t('trainerApplications.pleaseProvideReason'));
      return;
    }

    setProcessing(true);
    try {
      await api.post(`/admin/trainer-applications/${applicationId}/reject`, {
        reason: rejectionReason,
      });
      loadApplications();
      setSelectedApp(null);
      setRejectionReason('');
    } catch (err: any) {
      toast.error(err.response?.data?.error || t('trainerApplications.failedToReject'));
    } finally {
      setProcessing(false);
    }
  }

  const filteredApplications = applications.filter((app) =>
    filter === 'all' ? true : app.status === filter
  );

  const pendingCount = applications.filter((a) => a.status === 'pending').length;

  const filterLabels: Record<string, string> = {
    pending: t('trainerApplications.filterPending'),
    approved: t('trainerApplications.filterApproved'),
    rejected: t('trainerApplications.filterRejected'),
    all: t('trainerApplications.filterAll'),
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('trainerApplications.title')}</h1>
          <p className="text-gray-500 mt-1">
            {t('trainerApplications.subtitle')}
          </p>
        </div>
        {pendingCount > 0 && (
          <span className="bg-yellow-50 text-yellow-700 px-4 py-2 rounded-full text-sm font-medium">
            {t('trainerApplications.pendingCount', { count: pendingCount })}
          </span>
        )}
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-md mb-6">{error}</div>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {(['pending', 'approved', 'rejected', 'all'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 -mb-px ${
              filter === status
                ? 'border-b-2 border-gold text-gold font-medium'
                : 'text-gray-500 hover:text-gray-600'
            }`}
          >
            {filterLabels[status]}
            {status === 'pending' && pendingCount > 0 && (
              <span className="ml-2 bg-yellow-50 text-yellow-700 px-2 py-0.5 rounded-full text-xs">
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {filteredApplications.length === 0 ? (
        <div className="text-center py-12 glass-card rounded-lg">
          <p className="text-gray-500">{t('trainerApplications.noApplications', { filter: filter !== 'all' ? filterLabels[filter] : '' })}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredApplications.map((app) => (
            <div
              key={app._id}
              className="glass-card rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {app.user?.name || t('trainerApplications.unknownUser')}
                    </h3>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        app.status === 'pending'
                          ? 'bg-yellow-50 text-yellow-700'
                          : app.status === 'approved'
                          ? 'bg-green-50 text-green-700'
                          : 'bg-red-50 text-red-600'
                      }`}
                    >
                      {app.status}
                    </span>
                  </div>
                  <p className="text-gray-500 text-sm mb-2">{app.user?.email}</p>
                  <p className="text-gray-600 line-clamp-2">{app.bio}</p>

                  {app.expertise.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {app.expertise.map((exp, i) => (
                        <span
                          key={i}
                          className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs"
                        >
                          {exp}
                        </span>
                      ))}
                    </div>
                  )}

                  <p className="text-sm text-gray-500 mt-3">
                    {t('trainerApplications.submitted', { date: new Date(app.submittedAt).toLocaleDateString() })}
                  </p>
                </div>

                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => setSelectedApp(app)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100"
                  >
                    {t('trainerApplications.viewDetails')}
                  </button>
                  {app.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleApprove(app._id)}
                        disabled={processing}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                      >
                        {t('common.approve')}
                      </button>
                      <button
                        onClick={() => setSelectedApp(app)}
                        disabled={processing}
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                      >
                        {t('common.reject')}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {selectedApp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {selectedApp.user?.name}
                  </h2>
                  <p className="text-gray-500">{selectedApp.user?.email}</p>
                </div>
                <button
                  onClick={() => {
                    setSelectedApp(null);
                    setRejectionReason('');
                  }}
                  className="text-gray-500 hover:text-gray-600"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              <div className="space-y-6">
                {/* Status */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">{t('trainerApplications.statusLabel')}</h3>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      selectedApp.status === 'pending'
                        ? 'bg-yellow-50 text-yellow-700'
                        : selectedApp.status === 'approved'
                        ? 'bg-green-50 text-green-700'
                        : 'bg-red-50 text-red-600'
                    }`}
                  >
                    {selectedApp.status}
                  </span>
                </div>

                {/* Bio */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">
                    {t('trainerApplications.teachingBio')}
                  </h3>
                  <p className="text-gray-600 whitespace-pre-wrap">{selectedApp.bio}</p>
                </div>

                {/* Expertise */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">{t('trainerApplications.areasOfExpertise')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedApp.expertise.map((exp, i) => (
                      <span
                        key={i}
                        className="bg-blue-50 text-blue-700 px-3 py-1 rounded text-sm"
                      >
                        {exp}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Portfolio Links */}
                {selectedApp.portfolioLinks.length > 0 && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">{t('trainerApplications.portfolioLinks')}</h3>
                    <ul className="space-y-2">
                      {selectedApp.portfolioLinks.map((link, i) => (
                        <li key={i}>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gold hover:underline"
                          >
                            {link.label}: {link.url}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Documents */}
                {selectedApp.documents.length > 0 && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">{t('trainerApplications.uploadedDocuments')}</h3>
                    <ul className="space-y-2">
                      {selectedApp.documents.map((doc, i) => (
                        <li key={i} className="flex items-center gap-2 text-gray-600">
                          <span>&#128196;</span>
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gold hover:underline"
                          >
                            {doc.name}
                          </a>
                          <span className="text-gray-500 text-sm">({doc.type})</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Rejection reason (if rejected) */}
                {selectedApp.status === 'rejected' && selectedApp.rejectionReason && (
                  <div className="bg-red-50 p-4 rounded-md">
                    <h3 className="font-medium text-red-600 mb-2">{t('trainerApplications.rejectionReason')}</h3>
                    <p className="text-red-600">{selectedApp.rejectionReason}</p>
                  </div>
                )}

                {/* Submission date */}
                <div className="text-sm text-gray-500">
                  <p>{t('trainerApplications.submitted', { date: new Date(selectedApp.submittedAt).toLocaleString() })}</p>
                  {selectedApp.reviewedAt && (
                    <p>{t('trainerApplications.reviewed', { date: new Date(selectedApp.reviewedAt).toLocaleString() })}</p>
                  )}
                </div>

                {/* Actions for pending applications */}
                {selectedApp.status === 'pending' && (
                  <div className="border-t border-gray-200 pt-6">
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-600 mb-2">
                        {t('trainerApplications.rejectReasonLabel')}
                      </label>
                      <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gold"
                        rows={3}
                        placeholder={t('trainerApplications.rejectReasonPlaceholder')}
                      />
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleApprove(selectedApp._id)}
                        disabled={processing}
                        className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:opacity-50"
                      >
                        {processing ? t('common.processing') : t('trainerApplications.approveApplication')}
                      </button>
                      <button
                        onClick={() => handleReject(selectedApp._id)}
                        disabled={processing || !rejectionReason.trim()}
                        className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 disabled:opacity-50"
                      >
                        {processing ? t('common.processing') : t('trainerApplications.rejectApplication')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {confirmModal && (
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal(null)}
          onConfirm={confirmModal.onConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
          variant={confirmModal.variant}
          confirmText={t('common.approve')}
        />
      )}
    </div>
  );
}
